<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useEmitter } from 'dashboard/composables/emitter';
import { useStore } from 'dashboard/composables/store';
import { useEscClose } from 'dashboard/composables/useEscClose';
import { useTracking } from 'dashboard/composables/useTracking';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import {
  FEATURE_FLAGS,
  CAPTAIN_ACCESS_FEATURE_FLAGS,
  CAPTAIN_TOOLS_FEATURE_FLAGS,
} from 'dashboard/featureFlags';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import CaptainPaywall from 'dashboard/components-next/captain/pageComponents/Paywall.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AgentSection from './components/AgentSection.vue';
import AgentNotice from './components/AgentNotice.vue';
import AgentConfirmDialog from './components/AgentConfirmDialog.vue';
import AgentWorkspaceHeader from './components/AgentWorkspaceHeader.vue';
import AgentWorkspaceNavigation from './components/AgentWorkspaceNavigation.vue';
import AgentWorkspaceOverview from './components/AgentWorkspaceOverview.vue';
import AgentPerformanceSummary from './components/AgentPerformanceSummary.vue';
import AgentIdentitySection from './components/AgentIdentitySection.vue';
import AgentVoiceProfileSection from './components/AgentVoiceProfileSection.vue';
import AgentKnowledgeSection from './components/AgentKnowledgeSection.vue';
import AgentHandoffSection from './components/AgentHandoffSection.vue';
import AgentPublicationSection from './components/AgentPublicationSection.vue';
import AgentActivitySection from './components/AgentActivitySection.vue';
import AgentOperationsSection from './components/AgentOperationsSection.vue';
import AssistantDrilldownDrawer from 'dashboard/components-next/captain/pageComponents/overview/AssistantDrilldownDrawer.vue';
import { useAgentConfigurationForm } from './useAgentConfigurationForm';
import {
  faqLearningValidationError,
  identityValidation,
  publicationValidation,
} from './configurationValidation';
import { useAgentPerformance } from './useAgentPerformance';
import { useAgentPromptVersions } from './useAgentPromptVersions';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';
import { readPublicationPreflightError } from './publicationPreflight';
import { useAgentWorkspaceNavigation } from './useAgentWorkspaceNavigation';
import { useAgentActivity } from './useAgentActivity';
import { useAgentMemories } from './useAgentMemories';
import { useAgentPublicationLifecycle } from './useAgentPublicationLifecycle';
import {
  useAssistantManagement,
  useAgentWorkspacePresentation,
} from './useAgentWorkspacePresentation';
import { useAgentResourceOrchestration } from './useAgentResourceOrchestration';
import { useAgentSaveOperations } from './useAgentSaveOperations';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t: ce, te } = useI18n();
const { track } = useTracking();
const { checkPermissions, isFeatureFlagEnabled } = usePolicy();
const canManageAssistant = computed(() => checkPermissions(['administrator']));
const hasAdvancedManagement = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.CAPTAIN_ADVANCED_MANAGEMENT)
);
const hasAdvancedApprovals = computed(() =>
  isFeatureFlagEnabled(FEATURE_FLAGS.CAPTAIN_ADVANCED_APPROVALS)
);
const hasCaptainTools = computed(() =>
  isFeatureFlagEnabled(CAPTAIN_TOOLS_FEATURE_FLAGS)
);

const pageLayoutRef = ref(null);

const assistantId = computed(() => Number(route.params.assistantId || 0));
const agentPerformance = useAgentPerformance({ assistantId, t: ce });
const promptVersionsEnabled = computed(
  () => hasAdvancedManagement.value && canManageAssistant.value
);
const promptVersionHistory = useAgentPromptVersions({
  assistantId,
  enabled: promptVersionsEnabled,
  t: ce,
});
const assistants = computed(
  () => store.getters['captainAssistants/getRecords'] || []
);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);
const scenarios = computed(
  () => store.getters['captainScenarios/getRecords'] || []
);

const currentConfig = computed(() => assistant.value?.config || {});
const { assistantManagement, brandVoiceManagement } = useAssistantManagement({
  assistant,
  currentConfig,
});

const {
  identityForm,
  memoryForm,
  identityErrors,
  publicationErrors,
  baselineFormState,
  faqLearningManagement,
  faqLearningAuthorizedGroups,
  selectedFaqLearningSourceGroupIds,
  dirtySectionState,
  hasUnsavedChanges,
  updateIdentityField,
  updateMemoryField,
  toggleFaqGroup: handleToggleFaqGroup,
  markFormGroupsSaved,
  resetMemoryForm,
  syncIdentityForm,
  builders,
  faqGroupListToText,
} = useAgentConfigurationForm({
  assistant,
  management: assistantManagement,
  t: ce,
});

const buildIdentityConfig = builders.identity;
const buildBehaviorRules = builders.behavior;
const buildKnowledgeConfig = builders.knowledge;
const buildHandoffConfig = builders.handoff;
const buildPublicationSettingsConfig = builders.publicationSettings;

const {
  isLoading,
  loadError,
  resourceErrors,
  isInitialLoading,
  isRefreshing,
  hasResourceErrors,
  readResourceError: optionalResourceError,
  configureLoaders: configureResourceLoaders,
  fetchPageData,
  resetForAssistant: resetResourcesForAssistant,
  refreshActivityFromRealtime,
  dispose: disposeResourceOrchestration,
} = useAgentResourceOrchestration({
  assistantId,
  store,
  canManage: canManageAssistant,
  toolsEnabled: hasCaptainTools,
  approvalsEnabled: hasAdvancedApprovals,
  managementEnabled: hasAdvancedManagement,
  syncForm: syncIdentityForm,
  notify: useAlert,
  t: ce,
});

const {
  availableActions,
  runs,
  runsMeta,
  approvals: pendingApprovals,
  hasMoreRuns,
  pendingApprovalsCount,
  completedRunsCount,
  failedRunsCount,
  handoffRunsCount,
  isLoadingMoreRuns,
  isCancellingRun,
  runPendingCancel,
  selectedRunDetail,
  loadingRunId,
  fetchActions,
  fetchRuns,
  fetchApprovals,
  loadMoreRuns,
  requestCancelRun,
  cancelRunRequest,
  toggleRunDetails,
  cancelRun,
} = useAgentActivity({
  assistantId,
  canManage: canManageAssistant,
  approvalsEnabled: hasAdvancedApprovals,
  toolsEnabled: hasCaptainTools,
  api: CaptainAssistantAPI,
  notify: useAlert,
  t: ce,
  resourceErrors,
  readResourceError: optionalResourceError,
});

const {
  records: memories,
  totalCount: memoriesTotalCount,
  hasMore: hasMoreMemories,
  isLoadingMore: isLoadingMoreMemories,
  isSaving: isSavingMemory,
  isDeleting: isDeletingMemory,
  editingId: editingMemoryId,
  pendingDelete: memoryPendingDelete,
  fetchMemories,
  loadMore: loadMoreMemories,
  save: saveMemory,
  edit: editMemory,
  cancelEdit: cancelMemoryEdit,
  requestDelete: requestDeleteMemory,
  cancelDelete: cancelDeleteMemory,
  deleteMemory,
} = useAgentMemories({
  assistantId,
  canManage: canManageAssistant,
  enabled: hasAdvancedManagement,
  memoryForm,
  resetMemoryForm,
  api: CaptainAssistantAPI,
  notify: useAlert,
  t: ce,
  resourceErrors,
  readResourceError: optionalResourceError,
});

configureResourceLoaders({
  actions: targetAssistantId => fetchActions(targetAssistantId),
  runs: targetAssistantId =>
    fetchRuns(targetAssistantId ? { targetAssistantId } : undefined),
  approvals: targetAssistantId =>
    fetchApprovals(targetAssistantId ? { targetAssistantId } : undefined),
  memories: targetAssistantId => fetchMemories({ targetAssistantId }),
});

const {
  workspaceSections,
  localWorkspaceSectionIds,
  activeSection,
  selectedAssistantName,
  selectedAssistantDescription,
  deleteAssistantDescription,
  recentActivityItems,
  sourceCards,
  readinessChecklist,
  readinessPercent,
  assistantLifecycle,
  lifecycleStatus,
  lifecyclePresentation,
  healthPresentation,
  readinessLabel,
  readinessSummary,
  workspaceMetrics,
  improvementItems,
  previewResponse,
} = useAgentWorkspacePresentation({
  route,
  assistant,
  assistantManagement,
  identityForm,
  dirtySectionState,
  runs,
  runsMeta,
  memories,
  pendingApprovals,
  scenarios,
  availableActions,
  memoriesTotalCount,
  completedRunsCount,
  failedRunsCount,
  handoffRunsCount,
  canManage: canManageAssistant,
  managementEnabled: hasAdvancedManagement,
  approvalsEnabled: hasAdvancedApprovals,
  toolsEnabled: hasCaptainTools,
  t: ce,
  hasTranslation: te,
});

const focusField = async fieldId => {
  if (!fieldId) return;
  await nextTick();
  const target = document.getElementById(fieldId);
  target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  target?.focus();
};

const { openRoute, selectWorkspaceSection, reviewPublicationIssue } =
  useAgentWorkspaceNavigation({
    route,
    router,
    assistantId,
    workspaceSections,
    focusField,
  });

const applyValidation = (result, targetErrors) => {
  Object.assign(targetErrors, result.errors);
  if (!result.firstInvalid) return true;
  useAlert(result.firstInvalid.message);
  focusField(result.firstInvalid.fieldId);
  return false;
};

const validateIdentitySettings = () =>
  applyValidation(identityValidation(identityForm, ce), identityErrors);
const validatePublicationSettings = () =>
  applyValidation(publicationValidation(identityForm, ce), publicationErrors);
const validateFaqLearningSettings = () => {
  const errorKey = faqLearningValidationError({
    form: identityForm,
    authorizedGroups: faqLearningAuthorizedGroups.value,
    managedQuestionsGroup: faqLearningManagement.value.questionsGroup.jid,
  });
  if (!errorKey) return true;
  useAlert(ce(errorKey));
  return false;
};

const openCreateAssistantDialog = () => {
  if (!canManageAssistant.value) return;
  if (hasUnsavedChanges.value) {
    useAlert(ce('CAPTAIN_EXPERIENCE.WORKSPACE.SAVE_BEFORE_CREATING'));
    return;
  }
  pageLayoutRef.value?.openCreateAssistantDialog();
};

const {
  pendingNavigation,
  pendingRefresh,
  requestRefresh,
  closeUnsavedChangesDialog,
  discardUnsavedChanges,
} = useUnsavedChangesGuard({
  assistantId,
  hasUnsavedChanges,
  refresh: () => fetchPageData(),
  discardDraft: () => {
    editingMemoryId.value = null;
    resetMemoryForm({ markSaved: false });
  },
});

const {
  showDeleteAssistantModal,
  isDuplicatingAssistant,
  isDeletingAssistant,
  isSavingIdentity,
  isSavingBehavior,
  isSavingKnowledge,
  isSavingFaqLearning,
  isSavingHandoff,
  handleAssistantCreated,
  duplicateAssistant,
  requestDeleteAssistant,
  deleteAssistant,
  saveAssistantSection,
  saveIdentity,
  saveBehavior,
  saveKnowledgeSettings,
  saveFaqLearningSettings,
  saveHandoff,
} = useAgentSaveOperations({
  assistantId,
  assistant,
  assistants,
  currentConfig,
  activeSection,
  identityForm,
  hasUnsavedChanges,
  faqLearningManagement,
  faqGroupListToText,
  validateIdentity: validateIdentitySettings,
  validateFaqLearning: validateFaqLearningSettings,
  buildIdentityConfig,
  buildBehaviorRules,
  buildKnowledgeConfig,
  buildHandoffConfig,
  markSaved: markFormGroupsSaved,
  store,
  route,
  router,
  notify: useAlert,
  track,
  t: ce,
});

const {
  isSaving: isSavingPublish,
  showUnpublishConfirmation: showUnpublishAssistantModal,
  preflight: publicationPreflight,
  clearPreflight: clearPublicationPreflight,
  requestSave: requestSavePublishSettings,
  confirmUnpublish: confirmUnpublishAssistant,
  cancelUnpublish: cancelUnpublishAssistant,
} = useAgentPublicationLifecycle({
  assistantId,
  identityForm,
  lifecycleStatus,
  validate: validatePublicationSettings,
  buildSettings: buildPublicationSettingsConfig,
  saveSettings: saveAssistantSection,
  dispatch: (...args) => store.dispatch(...args),
  markSaved: markFormGroupsSaved,
  fetchVersions: promptVersionHistory.fetchVersions,
  reviewIssue: reviewPublicationIssue,
  readPreflightError: readPublicationPreflightError,
  notify: useAlert,
  track,
  trackEvent: ACCOUNT_EVENTS.CAPTAIN_AGENT_PUBLISH_SETTINGS_SAVED,
  t: ce,
});

watch(
  () => assistantId.value,
  newAssistantId => {
    clearPublicationPreflight();
    baselineFormState.value = {};
    resetResourcesForAssistant(newAssistantId);
  }
);
watch(
  () => assistant.value?.id,
  () => syncIdentityForm()
);
watch(
  [() => route.query.section, workspaceSections],
  ([requestedSection]) => {
    if (
      !requestedSection ||
      localWorkspaceSectionIds.value.has(String(requestedSection))
    ) {
      return;
    }

    router.replace({
      name: 'captain_assistants_experience_index',
      params: {
        accountId: route.params.accountId,
        assistantId: assistantId.value,
      },
      query: { ...route.query, section: 'overview' },
    });
  },
  { immediate: true }
);

watch(
  [assistantId, activeSection, promptVersionsEnabled],
  ([currentAssistantId, section, canLoadVersions]) => {
    if (currentAssistantId && section === 'publication' && canLoadVersions) {
      promptVersionHistory.fetchVersions();
    } else {
      promptVersionHistory.reset();
    }
  },
  { immediate: true }
);

useEmitter(
  BUS_EVENTS.CAPTAIN_AGENT_ACTIVITY_UPDATED,
  refreshActivityFromRealtime
);

useEscClose(showDeleteAssistantModal, () => {
  if (!isDeletingAssistant.value) showDeleteAssistantModal.value = false;
});

onBeforeUnmount(disposeResourceOrchestration);
onMounted(() => {
  fetchPageData();
});
</script>

<template>
  <PageLayout
    ref="pageLayoutRef"
    :header-title="ce('CAPTAIN_EXPERIENCE.AUTO.AGENTES_DE_IA')"
    :button-label="ce('CAPTAIN_EXPERIENCE.AUTO.NOVO_AGENTE')"
    :button-policy="['administrator']"
    :is-fetching="isInitialLoading"
    :loading-label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.LOADING_AGENT')"
    :show-pagination-footer="false"
    :show-know-more="false"
    :feature-flag="CAPTAIN_ACCESS_FEATURE_FLAGS"
    @click="openCreateAssistantDialog"
    @assistant-created="handleAssistantCreated"
  >
    <template #headerActions>
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-rotate-cw"
        :label="ce('CAPTAIN_EXPERIENCE.AUTO.ATUALIZAR')"
        :is-loading="isRefreshing"
        :disabled="isLoading"
        @click="requestRefresh"
      />
    </template>

    <template #paywall>
      <CaptainPaywall />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <AgentNotice
          v-if="loadError"
          :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.LOAD_ERROR_TITLE')"
          :description="loadError"
          tone="error"
          action-icon="i-lucide-refresh-cw"
          :action-label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.RETRY')"
          :is-loading="isLoading"
          @action="requestRefresh"
        />

        <template v-else-if="assistant?.id">
          <AgentWorkspaceHeader
            :name="selectedAssistantName"
            :description="selectedAssistantDescription"
            :lifecycle-label="lifecyclePresentation.label"
            :lifecycle-tone="lifecyclePresentation.tone"
            :health-label="healthPresentation.label"
            :health-tone="healthPresentation.tone"
            :readiness-label="readinessLabel"
            :readiness-percent="readinessPercent"
            :readiness-summary="readinessSummary"
            :can-manage="canManageAssistant"
            :can-test="canManageAssistant"
            :is-duplicating="isDuplicatingAssistant"
            :is-deleting="isDeletingAssistant"
            @test="openRoute('captain_assistants_playground_index')"
            @duplicate="duplicateAssistant"
            @delete="requestDeleteAssistant"
          />

          <AgentNotice
            v-if="!canManageAssistant"
            :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.READ_ONLY_TITLE')"
            :description="
              ce('CAPTAIN_EXPERIENCE.WORKSPACE.READ_ONLY_DESCRIPTION')
            "
            tone="info"
          />

          <AgentNotice
            v-if="hasUnsavedChanges"
            :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNSAVED_TITLE')"
            :description="
              ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNSAVED_DESCRIPTION')
            "
            tone="warning"
          />

          <AgentNotice
            v-if="hasResourceErrors"
            :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.PARTIAL_LOAD_TITLE')"
            :description="
              ce('CAPTAIN_EXPERIENCE.WORKSPACE.PARTIAL_LOAD_DESCRIPTION')
            "
            tone="warning"
            action-icon="i-lucide-refresh-cw"
            :action-label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.RETRY')"
            :is-loading="isLoading"
            @action="requestRefresh"
          />

          <div class="grid min-w-0 gap-6 lg:grid-cols-[14rem_minmax(0,1fr)]">
            <AgentWorkspaceNavigation
              :items="workspaceSections"
              :active-item="activeSection"
              :label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.NAVIGATION_LABEL')"
              :unsaved-label="
                ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNSAVED_SHORT_LABEL')
              "
              @select="selectWorkspaceSection"
            />

            <div class="min-w-0">
              <AgentWorkspaceOverview
                v-if="activeSection === 'overview'"
                :metrics="workspaceMetrics"
                :checklist="readinessChecklist"
                :improvements="improvementItems"
                :can-manage-scenarios="
                  hasAdvancedManagement && canManageAssistant
                "
                :can-use-tools="hasCaptainTools && canManageAssistant"
                :can-publish="hasAdvancedManagement && canManageAssistant"
                :can-test="canManageAssistant"
                @select-section="selectWorkspaceSection"
                @open-route="openRoute"
              />

              <AgentPerformanceSummary
                v-if="activeSection === 'overview'"
                v-model:selected-range="agentPerformance.selectedRange.value"
                class="mt-6"
                :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.RESULTS_TITLE')"
                :metrics="agentPerformance.metrics.value"
                :is-loading="agentPerformance.isLoading.value"
                :error="agentPerformance.error.value"
                :loading-label="
                  ce('CAPTAIN_EXPERIENCE.WORKSPACE.RESULTS_LOADING')
                "
                :error-title="
                  ce('CAPTAIN_EXPERIENCE.WORKSPACE.RESULTS_ERROR_TITLE')
                "
                :retry-label="ce('CAPTAIN_EXPERIENCE.WORKSPACE.RETRY')"
                :can-drilldown="canManageAssistant"
                @retry="agentPerformance.fetchStats"
                @open-metric="agentPerformance.openMetric"
              />

              <AgentIdentitySection
                v-if="activeSection === 'identity'"
                :form="identityForm"
                :errors="identityErrors"
                :can-manage="canManageAssistant"
                :has-advanced-management="hasAdvancedManagement"
                :brand-voice="brandVoiceManagement"
                :preview-response="previewResponse"
                :is-saving="isSavingIdentity"
                :is-saving-behavior="isSavingBehavior"
                @update-field="updateIdentityField"
                @save="saveIdentity"
                @save-behavior="saveBehavior"
                @open-route="openRoute"
              />

              <AgentVoiceProfileSection
                v-if="activeSection === 'voice' && canManageAssistant"
                :can-manage="canManageAssistant"
              />

              <AgentKnowledgeSection
                v-if="activeSection === 'knowledge'"
                :form="identityForm"
                :memory-form="memoryForm"
                :source-cards="sourceCards"
                :improvement-items="improvementItems"
                :faq-learning-management="faqLearningManagement"
                :selected-faq-group-ids="selectedFaqLearningSourceGroupIds"
                :can-manage="canManageAssistant"
                :has-advanced-management="
                  hasAdvancedManagement && canManageAssistant
                "
                :is-saving-knowledge="isSavingKnowledge"
                :is-saving-faq-learning="isSavingFaqLearning"
                :is-saving-memory="isSavingMemory"
                :editing-memory-id="editingMemoryId"
                :memories="memories"
                :memories-total-count="memoriesTotalCount"
                :has-more-memories="hasMoreMemories"
                :is-loading-more-memories="isLoadingMoreMemories"
                @update-field="updateIdentityField"
                @update-memory-field="updateMemoryField"
                @toggle-faq-group="handleToggleFaqGroup"
                @save-knowledge="saveKnowledgeSettings"
                @save-faq-learning="saveFaqLearningSettings"
                @save-memory="saveMemory"
                @cancel-memory-edit="cancelMemoryEdit"
                @edit-memory="editMemory"
                @delete-memory="requestDeleteMemory"
                @load-more-memories="loadMoreMemories"
                @open-route="openRoute"
              />

              <AgentHandoffSection
                v-if="activeSection === 'handoff'"
                :form="identityForm"
                :errors="publicationErrors"
                :can-manage="canManageAssistant"
                :is-saving="isSavingHandoff"
                :has-advanced-approvals="hasAdvancedApprovals"
                :pending-approvals-count="pendingApprovalsCount"
                @update-field="updateIdentityField"
                @save="saveHandoff"
                @open-route="openRoute"
              />

              <AgentPublicationSection
                v-if="activeSection === 'publication'"
                :form="identityForm"
                :errors="publicationErrors"
                :teams="teams"
                :can-manage="canManageAssistant"
                :is-saving="isSavingPublish"
                :readiness-checklist="readinessChecklist"
                :lifecycle-status="lifecycleStatus"
                :has-unpublished-changes="
                  Boolean(assistantLifecycle.has_unpublished_changes)
                "
                :published-version-id="assistantLifecycle.published_version_id"
                :published-at="assistantLifecycle.published_at"
                :versions="promptVersionHistory.versions.value"
                :is-loading-versions="promptVersionHistory.isLoading.value"
                :versions-error="promptVersionHistory.error.value"
                :preflight-blockers="publicationPreflight.blockers"
                :preflight-warnings="publicationPreflight.warnings"
                @update-field="updateIdentityField"
                @save="requestSavePublishSettings"
                @retry-versions="promptVersionHistory.fetchVersions"
                @open-route="openRoute"
                @review-preflight-issue="reviewPublicationIssue"
              />

              <AgentActivitySection
                v-if="activeSection === 'activity'"
                :runs="runs"
                :can-manage="canManageAssistant"
                :selected-run-detail="selectedRunDetail"
                :loading-run-id="loadingRunId"
                :has-more-runs="hasMoreRuns"
                :is-loading-more-runs="isLoadingMoreRuns"
                :recent-activity-items="recentActivityItems"
                @cancel-run="requestCancelRun"
                @toggle-run-details="toggleRunDetails"
                @load-more-runs="loadMoreRuns"
              />

              <AgentOperationsSection
                v-if="activeSection === 'operations'"
                :assistant-id="assistantId"
                :runs="runs"
                :can-manage="canManageAssistant"
              />
            </div>
          </div>
        </template>

        <AgentSection v-else>
          <div
            class="flex min-h-80 flex-col items-center justify-center text-center"
          >
            <span class="i-lucide-bot size-12 text-n-slate-9" />
            <h2 class="mt-4 text-xl font-medium text-n-slate-12">
              {{ ce('CAPTAIN_EXPERIENCE.AUTO.CRIE_SEU_PRIMEIRO_AGENTE') }}
            </h2>
            <p class="mt-2 max-w-lg text-sm text-n-slate-11">
              {{
                ce(
                  'CAPTAIN_EXPERIENCE.AUTO.USE_O_PAINEL_AO_LADO_PARA_CRIAR_UM_AGENTE_PERSONALIZADO_'
                )
              }}
            </p>
            <Button
              v-if="canManageAssistant"
              class="mt-5"
              icon="i-lucide-plus"
              :label="ce('CAPTAIN_EXPERIENCE.AUTO.NOVO_AGENTE')"
              @click="openCreateAssistantDialog"
            />
          </div>
        </AgentSection>
      </div>
    </template>

    <AgentConfirmDialog
      :show="Boolean(pendingNavigation || pendingRefresh)"
      :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNSAVED_DIALOG_TITLE')"
      :description="
        ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNSAVED_DIALOG_DESCRIPTION')
      "
      :confirm-text="ce('CAPTAIN_EXPERIENCE.WORKSPACE.DISCARD_AND_CONTINUE')"
      :cancel-text="ce('CAPTAIN_EXPERIENCE.WORKSPACE.KEEP_EDITING')"
      @close="closeUnsavedChangesDialog"
      @confirm="discardUnsavedChanges"
    />

    <AgentConfirmDialog
      :show="showUnpublishAssistantModal"
      :title="ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNPUBLISH_CONFIRM_TITLE')"
      :description="
        ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNPUBLISH_CONFIRM_DESCRIPTION')
      "
      :confirm-text="
        ce('CAPTAIN_EXPERIENCE.WORKSPACE.UNPUBLISH_CONFIRM_ACTION')
      "
      :cancel-text="ce('CAPTAIN_EXPERIENCE.WORKSPACE.KEEP_EDITING')"
      :is-loading="isSavingPublish"
      @close="cancelUnpublishAssistant"
      @confirm="confirmUnpublishAssistant"
    />

    <AgentConfirmDialog
      :show="Boolean(runPendingCancel)"
      :title="ce('CAPTAIN_EXPERIENCE.SCRIPT.CANCEL_RUN')"
      :description="ce('CAPTAIN_EXPERIENCE.SCRIPT.CANCEL_RUN_CONFIRMATION')"
      :confirm-text="ce('CAPTAIN_EXPERIENCE.SCRIPT.CANCEL_RUN')"
      :is-loading="isCancellingRun"
      @close="cancelRunRequest"
      @confirm="cancelRun"
    />

    <AgentConfirmDialog
      :show="Boolean(memoryPendingDelete)"
      :title="ce('CAPTAIN_EXPERIENCE.SCRIPT.DELETE_MEMORY')"
      :description="ce('CAPTAIN_EXPERIENCE.SCRIPT.DELETE_MEMORY_CONFIRMATION')"
      :confirm-text="ce('CAPTAIN_EXPERIENCE.AUTO.EXCLUIR')"
      :is-loading="isDeletingMemory"
      @close="cancelDeleteMemory"
      @confirm="deleteMemory"
    />

    <AgentConfirmDialog
      :show="showDeleteAssistantModal"
      :title="ce('CAPTAIN_EXPERIENCE.AUTO.EXCLUIR_AGENTE')"
      :description="deleteAssistantDescription"
      :confirm-text="ce('CAPTAIN_EXPERIENCE.AUTO.EXCLUIR')"
      :is-loading="isDeletingAssistant"
      @close="showDeleteAssistantModal = false"
      @confirm="deleteAssistant"
    />

    <AssistantDrilldownDrawer
      v-if="canManageAssistant"
      :open="agentPerformance.isDrilldownOpen.value"
      :assistant-id="assistantId"
      :metric="agentPerformance.selectedMetric.value.metric"
      :metric-name="agentPerformance.selectedMetric.value.label"
      :metric-value="agentPerformance.selectedMetric.value.value"
      :range="agentPerformance.selectedRange.value"
      @close="agentPerformance.closeDrilldown"
    />
  </PageLayout>
</template>
